import { useState } from 'react';
import { usePantry, usePantryAction, useStockPantry } from '../api/planner';
import type { IngredientOption, PantryItem, PantryVerb } from '../api/planner';
import ConfirmDialog from '../components/ConfirmDialog';
import IngredientPicker from '../components/IngredientPicker';
import StockFields from '../components/StockFields';

function expiryLabel(item: PantryItem) {
  if (item.days_until_expiry === null) {
    if (item.dated_by_packet) return 'use by the date on the pack';
    return item.frozen ? 'frozen, no expiry tracked' : 'no expiry tracked';
  }
  if (item.days_until_expiry < 0) return `${Math.abs(item.days_until_expiry)} days past`;
  if (item.days_until_expiry === 0) return 'use today';
  if (item.days_until_expiry === 1) return 'use tomorrow';
  // Months, past the point where a day count means anything: a bag of chicken
  // reading "358 days left" is a number nobody converts in their head.
  if (item.days_until_expiry > 90) {
    return `good for about ${Math.round(item.days_until_expiry / 30)} months`;
  }
  return `${item.days_until_expiry} days left`;
}

type Verb = {
  /** Whether the verb means anything for this row; bulk actions skip the rest. */
  applies: (item: PantryItem) => boolean;
  /** The row button, when it has one. Expiry is edited from its own cell. */
  rowLabel?: (item: PantryItem) => string;
  bulkLabel: string;
  title: (what: string) => string;
  body: string;
  confirm: string;
};

const VERBS: Record<PantryVerb, Verb> = {
  open: {
    applies: (item) => !item.opened_date && !item.is_expired && !item.frozen,
    rowLabel: () => 'Opened',
    bulkLabel: 'Opened',
    title: (what) => `Mark ${what} opened?`,
    body: 'The expiry date will be recalculated from today.',
    confirm: 'Mark opened',
  },
  // The way out of "this expires tomorrow" for something that does not: a bag
  // of chicken breasts keeps a day in the fridge and a year in the freezer.
  // Offered only where there is a freezer time to re-date it from.
  freeze: {
    applies: (item) => !item.is_expired && item.freezable && !item.frozen,
    rowLabel: () => 'Freeze',
    bulkLabel: 'Freeze',
    title: (what) => `Move ${what} to the freezer?`,
    body: 'The expiry date will switch to how long it keeps frozen.',
    confirm: 'Move to freezer',
  },
  thaw: {
    applies: (item) => !item.is_expired && item.frozen,
    rowLabel: () => 'Thaw',
    bulkLabel: 'Thaw',
    title: (what) => `Mark ${what} thawed?`,
    body: 'The expiry date will be recalculated from today.',
    confirm: 'Mark thawed',
  },
  // The same endpoint either way — the pantry only cares that it is gone.
  // What differs is why, and the button should say which.
  deplete: {
    applies: () => true,
    rowLabel: (item) => (item.is_expired ? 'Tossed it' : 'Used up'),
    bulkLabel: 'Remove',
    title: (what) => `Remove ${what} from the pantry?`,
    body: 'This cannot be undone.',
    confirm: 'Remove',
  },
  expiry: {
    applies: () => true,
    bulkLabel: 'Change expiry',
    title: (what) => `Change the expiry date for ${what}`,
    body: 'Opening, freezing or thawing it later will recalculate this date.',
    confirm: 'Save date',
  },
};

const ROW_VERBS: PantryVerb[] = ['open', 'freeze', 'thaw', 'deplete'];
const BULK_VERBS: PantryVerb[] = ['open', 'freeze', 'thaw', 'expiry', 'deplete'];

type Pending = { verb: PantryVerb; items: PantryItem[]; skipped: number };

export default function Pantry() {
  const { data: items, isLoading } = usePantry();
  const action = usePantryAction();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [pending, setPending] = useState<Pending | null>(null);

  if (isLoading) return <p className="center-note">Loading the pantry…</p>;

  const stock = items ?? [];
  // Filtered through the live list, so a row removed elsewhere drops out.
  const picked = stock.filter((item) => selected.has(item.id));

  // Three states, not two. Something past its date is not stock the planner
  // should build a week around — it is a thing to throw away, and it stays
  // listed only so you know to go and do that.
  const groups = [
    {
      key: 'expired',
      heading: 'Throw these out',
      note: 'Past their date, so the planner no longer counts them and the shopping list will buy them again.',
      items: stock.filter((item) => item.is_expired),
    },
    {
      key: 'urgent',
      heading: 'Use these soon',
      items: stock.filter((item) => item.is_expiring_soon && !item.is_expired),
    },
    {
      key: 'stock',
      heading: 'In stock',
      items: stock.filter((item) => !item.is_expiring_soon && !item.is_expired),
    },
  ].filter((group) => group.items.length > 0);

  function toggle(rows: PantryItem[], on: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      for (const row of rows) {
        if (on) next.add(row.id);
        else next.delete(row.id);
      }
      return next;
    });
  }

  function ask(verb: PantryVerb, rows: PantryItem[]) {
    const eligible = rows.filter(VERBS[verb].applies);
    action.reset();
    setPending({ verb, items: eligible, skipped: rows.length - eligible.length });
  }

  function run(expirationDate?: string) {
    if (!pending) return;
    const ids = pending.items.map((item) => item.id);
    action.mutate(
      { ids, verb: pending.verb, expirationDate },
      {
        onSuccess: () => {
          setPending(null);
          toggle(pending.items, false);
        },
      },
    );
  }

  return (
    <section>
      <h1>Pantry</h1>

      <AddToPantry stock={stock} />

      {stock.length === 0 && (
        <p className="muted">
          Nothing tracked yet. Add what is on the shelves above, or let items
          land here as you shop.
        </p>
      )}

      {picked.length > 0 && (
        <div className="pantry-bulk" role="toolbar" aria-label="Selected items">
          <span className="pantry-bulk__count">{picked.length} selected</span>
          {BULK_VERBS.map((verb) => {
            const count = picked.filter(VERBS[verb].applies).length;
            return (
              <button
                key={verb}
                type="button"
                className="btn btn--small"
                disabled={count === 0}
                onClick={() => ask(verb, picked)}
              >
                {VERBS[verb].bulkLabel}
                {count !== picked.length && count > 0 ? ` (${count})` : ''}
              </button>
            );
          })}
          <button
            type="button"
            className="pantry-bulk__clear"
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      {groups.length > 0 && (
        <table className="pantry-table">
          <thead>
            <tr>
              <th className="pantry-table__check">
                <Checkbox
                  label="Select everything"
                  checked={picked.length === stock.length}
                  indeterminate={picked.length > 0 && picked.length < stock.length}
                  onChange={(on) => toggle(stock, on)}
                />
              </th>
              <th>Item</th>
              <th className="pantry-table__amount">Amount</th>
              <th>Expires</th>
              <th>
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          {groups.map((group) => {
            const ticked = group.items.filter((item) => selected.has(item.id)).length;
            return (
              <tbody key={group.key} className={`is-${group.key}`}>
                <tr className="pantry-table__group">
                  <td className="pantry-table__check">
                    <Checkbox
                      label={`Select all in ${group.heading}`}
                      checked={ticked === group.items.length}
                      indeterminate={ticked > 0 && ticked < group.items.length}
                      onChange={(on) => toggle(group.items, on)}
                    />
                  </td>
                  <th colSpan={4} scope="colgroup">
                    <h2>{group.heading}</h2>
                    {group.note && <span className="muted small">{group.note}</span>}
                  </th>
                </tr>
                {group.items.map((item) => (
                  <Row
                    key={item.id}
                    item={item}
                    checked={selected.has(item.id)}
                    onCheck={(on) => toggle([item], on)}
                    onAction={(verb) => ask(verb, [item])}
                  />
                ))}
              </tbody>
            );
          })}
        </table>
      )}

      {pending && (
        <ConfirmAction
          pending={pending}
          busy={action.isPending}
          error={action.isError ? (action.error as Error).message : undefined}
          onConfirm={run}
          onCancel={() => setPending(null)}
        />
      )}
    </section>
  );
}

/** A checkbox that can also show "some of these", for the select-all boxes. */
function Checkbox({
  label,
  checked,
  indeterminate = false,
  onChange,
}: {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      ref={(box) => {
        if (box) box.indeterminate = indeterminate;
      }}
      onChange={(event) => onChange(event.target.checked)}
    />
  );
}

function ConfirmAction({
  pending,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  pending: Pending;
  busy: boolean;
  error?: string;
  onConfirm: (expirationDate?: string) => void;
  onCancel: () => void;
}) {
  const { verb, items, skipped } = pending;
  const spec = VERBS[verb];
  const single = items.length === 1;
  const [date, setDate] = useState(
    single && items[0].expiration_date ? items[0].expiration_date : '',
  );
  const needsDate = verb === 'expiry';
  const shown = items.slice(0, 8);

  return (
    <ConfirmDialog
      titleId="confirm-pantry-title"
      title={spec.title(single ? items[0].name : `${items.length} items`)}
      onCancel={onCancel}
      actions={
        <>
          <button
            className="btn btn--primary"
            disabled={busy || (needsDate && !date)}
            onClick={() => onConfirm(needsDate ? date : undefined)}
          >
            {single && spec.rowLabel ? spec.rowLabel(items[0]) : spec.confirm}
          </button>
          <button className="link-button" onClick={onCancel}>
            Back
          </button>
        </>
      }
    >
      {!single && (
        <p className="small">
          {shown.map((item) => item.name).join(', ')}
          {items.length > shown.length && `, and ${items.length - shown.length} more`}
        </p>
      )}
      {needsDate && (
        <label className="pantry-expiry-field">
          New expiry date
          <input
            type="date"
            value={date}
            autoFocus
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
      )}
      <p className="muted small">{spec.body}</p>
      {skipped > 0 && (
        <p className="muted small">
          {skipped} other selected item{skipped === 1 ? '' : 's'} will be left as{' '}
          {skipped === 1 ? 'it is' : 'they are'}.
        </p>
      )}
      {error && <p className="error small">{error}</p>}
    </ConfirmDialog>
  );
}

/** Put something in the pantry that no shopping list mentioned.
 *
 * Every other way in starts from a generated list, which makes the one job
 * worth doing before a week is planned — walking the shelves and saying what
 * is actually on them — the one job the pantry could not do. This is that,
 * and it is built for a sitting rather than for a single jar: the panel stays
 * open, and each add returns it to the search box for the next thing.
 *
 * Only ingredients some recipe calls for are offered. A pantry row for
 * anything else cannot reach a plan or a shopping list, so it would be a row
 * that sits there doing nothing, and the search already hides them.
 */
function AddToPantry({ stock }: { stock: PantryItem[] }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<IngredientOption | null>(null);
  const [added, setAdded] = useState<string[]>([]);
  // Bumped on every add so `StockFields` remounts with empty fields. Each jar
  // is its own set of answers, and a date carried over from the last one is a
  // wrong date nobody was asked to confirm.
  const [round, setRound] = useState(0);

  const add = useStockPantry();

  function stocked(option: IngredientOption) {
    return stock.filter((item) => item.ingredient === option.id);
  }

  return (
    <div className="card pantry-add">
      <button
        type="button"
        className="btn btn--wide"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? 'Done adding' : '+ Add something'}
      </button>

      {open && (
        <>
          {picked ? (
            <>
              <p className="pantry-add__picked">
                {picked.name}{' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setPicked(null)}
                >
                  change
                </button>
              </p>
              <StockFields
                key={`${picked.id}-${round}`}
                name={picked.name}
                defaultUnit={picked.default_unit}
                packHint="Leave the amount blank if it is a whole, unopened one."
                pending={add.isPending}
                error={add.isError ? (add.error as Error).message : undefined}
                onSubmit={(row) =>
                  add.mutate(
                    { ...row, ingredient_id: picked.id, note: 'found in the pantry' },
                    {
                      onSuccess: () => {
                        setAdded((names) => [...names, picked.name]);
                        setPicked(null);
                        setRound((n) => n + 1);
                      },
                    },
                  )
                }
              />
            </>
          ) : (
            <IngredientPicker
              placeholder="What is on the shelf?"
              excludeArtifacts
              autoFocus
              onPick={setPicked}
              // What is already tracked, so a second jar is a decision rather
              // than an accident. Two half-jars of capers are legitimately two
              // rows — each has its own date — so this informs and does not block.
              meta={(option) => {
                const held = stocked(option);
                return held.length > 0
                  ? `${held.length} already in the pantry`
                  : `${option.recipes} recipes`;
              }}
            />
          )}

          {/* The list below has already refreshed, but during a sweep it is
              long and the confirmation needs to be where the eyes are. */}
          {added.length > 0 && (
            <p className="muted small" aria-live="polite">
              Added: {added.join(' · ')}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Row({
  item,
  checked,
  onCheck,
  onAction,
}: {
  item: PantryItem;
  checked: boolean;
  onCheck: (on: boolean) => void;
  onAction: (verb: PantryVerb) => void;
}) {
  const amount = item.quantity_display
    ? `${item.quantity_display} ${item.unit}`
    : item.note || 'some';
  return (
    <tr className={checked ? 'is-selected' : undefined}>
      <td className="pantry-table__check">
        <Checkbox label={`Select ${item.name}`} checked={checked} onChange={onCheck} />
      </td>
      <td className="pantry-table__name">
        {item.name}
        {item.frozen && <span className="tag tag--frozen">frozen</span>}
        {/* The amount column is dropped on phones; it rides under the name. */}
        <span className="pantry-table__amount-inline muted small">{amount}</span>
      </td>
      <td className="pantry-table__amount muted">{amount}</td>
      <td className="pantry-table__expiry">
        <button
          type="button"
          className="pantry-table__edit"
          title={item.expiration_date ?? undefined}
          aria-label={`Change expiry date for ${item.name}: ${expiryLabel(item)}`}
          onClick={() => onAction('expiry')}
        >
          {expiryLabel(item)}
          <span aria-hidden="true"> ✎</span>
        </button>
      </td>
      <td className="pantry-table__actions">
        {ROW_VERBS.filter((verb) => VERBS[verb].applies(item)).map((verb) => (
          <button
            key={verb}
            type="button"
            className="btn btn--small"
            onClick={() => onAction(verb)}
          >
            {VERBS[verb].rowLabel?.(item)}
          </button>
        ))}
      </td>
    </tr>
  );
}
